import { Typography } from '../primitives/index.js'
import { TreeItem as MuiTreeItem, SimpleTreeView } from '../primitives/tree-view.js'
import { graphHooks } from '@tunasong/graph-lib/react'
import { ArrowDropDown, ArrowRight } from '@tunasong/icons'
import type { FilterFunc } from '@tunasong/models'
import { folderSort, isHiddenSysEntity, isTopLevelEntity, logger } from '@tunasong/models'
import { getElementMedia, usePlugins } from '@tunasong/plugin-lib'
import type { Entity, Persisted } from '@tunasong/schemas'
import type { FC } from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { DndProvider } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import { EMPTY_NODE, LOADING_NODE } from './constants.js'
import type { EntityTreeItemProps } from './entity-tree-item.js'
import { EntityTreeItem } from './entity-tree-item.js'

export interface EntityTreeProps {
  /**  */
  includeRoot?: boolean
  rootEntities: Persisted<Entity>[]
  /** @default show all top-level entities */
  filter?: FilterFunc
  /** Entity Ids that should not be selectable */
  disableIds?: string[]
  /**
   * Why an entity can't be chosen, e.g. a folder the user can't write to. Such an entity stays listed, disabled, with
   * the reason: never silently missing (GH #2450).
   */
  disabledReason?: (entity: Persisted<Entity>) => string | undefined
  /** Selected item */
  selected: Persisted<Entity> | undefined
  TreeItem?: FC<EntityTreeItemProps>
  onSelect(entity?: Persisted<Entity>): void
  onSelectRoot?(): void
}

export const EntityTree: FC<EntityTreeProps> = props => {
  const {
    includeRoot = false,
    rootEntities,
    filter = isTopLevelEntity,
    selected,
    onSelectRoot,
    onSelect,
    TreeItem = EntityTreeItem,
    disableIds,
    disabledReason,
    ...restProps
  } = props

  const [expanded, setExpanded] = useState<string[]>(['ROOT'])

  /** If filter is specified, load all child entities and filter using that function. Otherwise, filter on type */
  const filterType = props.filter ? undefined : 'folder'

  const plugins = usePlugins('all')
  // We need to track the loaded children to ensure that RTK Query does not release the cache
  const [entityChildren, setEntityChildren] = useState<Record<string, Persisted<Entity>[]>>({
    ROOT: rootEntities,
  })

  /** We use the result here to ensure we refresh the UI on changes */
  const [loadChildEntitiesBatch] = graphHooks.useLoadChildEntitiesBatch()

  const [loadEntity] = graphHooks.useLoadEntity()

  const loadChildrenBatch = useCallback(
    async (parentIds: string[]) => {
      const idsToLoad = parentIds.filter(id => !entityChildren[id])
      if (idsToLoad.length === 0) {
        return
      }
      try {
        const { data: children } = await loadChildEntitiesBatch({ parentIds: idsToLoad, type: filterType }, true)
        if (children) {
          const grouped: Record<string, Persisted<Entity>[]> = {}
          for (const id of idsToLoad) {
            grouped[id] = []
          }
          for (const child of children) {
            if (child.parentId && grouped[child.parentId]) {
              grouped[child.parentId].push(child)
            }
          }
          setEntityChildren(prev => ({ ...prev, ...grouped }))
        }
      } catch (err) {
        logger.warn('Failed to batch load children', idsToLoad, err)
      }
    },
    [entityChildren, filterType, loadChildEntitiesBatch]
  )

  /** Ensure that all parents of selected item are expanded */
  const ancestors = graphHooks.useAncestors(selected)
  const allExpanded = [
    ...new Set([
      ...expanded,
      /* selected?.id, */ ...Object.values(ancestors)
        .map(e => e.id)
        .filter(Boolean),
    ]),
  ]
  /** Always load the root, expanded, and disabledIDs. We need to load disabled because expanding will not be possible  */
  const initialLoad = useRef(false)
  useEffect(() => {
    if (initialLoad.current || rootEntities.length === 0) {
      return
    }
    const rootIds = [...rootEntities.map(e => e.id), ...(disableIds ?? [])]
    void loadChildrenBatch(rootIds)
    initialLoad.current = true
  }, [disableIds, loadChildrenBatch, rootEntities])

  const getChildrenElements = useCallback(
    (entity: Persisted<Entity>) => {
      /** Use local state as source of truth — RTK Query cache can be GC'd when lazy query subscriptions are lost */
      const loadedChildren = entityChildren[entity.id] ?? []
      if (loadedChildren.length === 0) {
        /** @note if children is null then the tree will not have an expand button */
        return null
      }

      const children = loadedChildren
        .filter(e => e.parentId === entity.id)
        .filter(e => !isHiddenSysEntity(e))
        .filter(filter)
        .sort()
      const e = folderSort(children.filter(filter))
      if (e.length === 0) {
        return null
      }
      return e.map(child => {
        const { icon, materialColor } = getElementMedia(child.type, plugins)
        return (
          <TreeItem
            key={child.id}
            entity={child}
            /** We open up for disabled items */
            open={disableIds?.includes(child.id) || child === selected}
            excluded={disableIds?.includes(child.id)}
            disabledReason={disabledReason?.(child)}
            Icon={icon}
            color={materialColor}
          >
            {getChildrenElements(child)}
          </TreeItem>
        )
      })
    },
    [TreeItem, disableIds, disabledReason, entityChildren, filter, plugins, selected]
  )

  const handleToggle = async (ev: unknown, itemId: string) => {
    if (!itemId) {
      return
    }
    if (expanded.some(i => i === itemId)) {
      setExpanded(expanded.filter(id => id !== itemId))
      return
    }
    const allExpanded = [...new Set([...expanded, itemId])].filter(Boolean)
    setExpanded(allExpanded)

    /** Load children for all expanded nodes in one batch */
    const nodeIds = allExpanded.filter(id => id !== 'ROOT')
    await loadChildrenBatch(nodeIds)

    /** Load grandchildren in one batch as well */
    const childIds = nodeIds.flatMap(id => (entityChildren[id] ?? []).map(c => c.id))
    if (childIds.length > 0) {
      void loadChildrenBatch(childIds)
    }
  }

  const handleSelect = useCallback(
    async (ev: unknown, itemId: string | null) => {
      /** Loading nodes have  */
      const ignoreNodes = [LOADING_NODE, EMPTY_NODE, ...(disableIds ?? [])]
      if (itemId === 'ROOT' && onSelectRoot) {
        onSelectRoot()
        return
      }
      if (onSelect && itemId) {
        try {
          const { data: entity } = await loadEntity({ id: itemId }, true)
          /** A disabled entity is listed, never chosen, and clicking it leaves an earlier pick alone */
          if (entity && disabledReason?.(entity)) {
            return
          }
          onSelect(ignoreNodes.includes(itemId) ? undefined : entity)
        } catch (err) {
          logger.warn('Failed to load entity', itemId, err)
        }
      }
    },
    [disableIds, disabledReason, loadEntity, onSelect, onSelectRoot]
  )

  /** @note we don't memo the tree-items because we can receive new data at any time  */

  const items = (
    <>
      {includeRoot && (
        <MuiTreeItem key={'root'} itemId={'ROOT'} label={<Typography variant="subtitle1">Top</Typography>} />
      )}
      {folderSort(rootEntities.filter(filter)).map(entity => {
        const { icon, materialColor: color } = getElementMedia(entity.type, plugins)
        return (
          <TreeItem
            key={entity.id}
            entity={entity}
            open={Boolean(disableIds?.includes(entity.id) || (entity && allExpanded.find(id => id === entity.id)))}
            excluded={disableIds?.includes(entity.id)}
            disabledReason={disabledReason?.(entity)}
            Icon={icon}
            color={color}
          >
            {getChildrenElements(entity)}
          </TreeItem>
        )
      })}
    </>
  )

  return (
    <DndProvider backend={HTML5Backend}>
      <SimpleTreeView
        onItemSelectionToggle={handleToggle}
        onItemExpansionToggle={handleToggle}
        onSelectedItemsChange={handleSelect}
        expandedItems={expanded}
        selectedItems={selected?.id ?? ''}
        slots={{
          collapseIcon: ArrowDropDown,
          expandIcon: ArrowRight,
        }}
        /** we don't use multiselect yet */
        {...restProps}
      >
        {items}
      </SimpleTreeView>
    </DndProvider>
  )
}

export default EntityTree
